/**
 * useSystemStatus — ONE place that answers "is everything up?".
 *
 * Feeds the About panel (Help → About), the pop-out gate, and anything else
 * that needs to show an FSR whether the platform is healthy. Two sources:
 *
 *   1. GET /health/dependencies — the server's own probe of sql, jxrl (Core
 *      Director), ddp (LexisNexis), usps, signnow, graph, dataverse. One call
 *      covers all of them, and the server does the work; the client just
 *      renders. Note the `ddp` probe is CONFIG-ONLY and never spends a
 *      LexisNexis query — DDP bills per call.
 *   2. The Scanner Bridge, which is LOCAL (PCBTwainBridge on :18625) and so
 *      cannot be probed from the server at all.
 *
 * A dependency the server does not report comes back 'unknown', never 'down'.
 * "We did not measure this" and "this is broken" are different answers, and
 * showing the second when you mean the first is how a healthy platform gets
 * reported as an outage.
 */
import { useCallback, useEffect, useState } from 'react';
import type { DependenciesHealthResponse } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';

export type StatusValue = 'ok' | 'down' | 'unknown';

export interface SystemStatus {
  coreDirector: StatusValue;
  lexisNexis: StatusValue;
  usps: StatusValue;
  scannerBridge: StatusValue;
  /** Raw per-dependency detail, for the expanded view. */
  detail: Record<string, { status: StatusValue; durationMs?: number; error?: string | null }>;
  loading: boolean;
  refresh: () => void;
}

const SCANNER_BRIDGE_URL = 'http://localhost:18625/health';
const SCANNER_TIMEOUT_MS = 1500;

function toStatus(raw: string | undefined): StatusValue {
  if (!raw) return 'unknown';
  return raw.toLowerCase() === 'ok' ? 'ok' : 'down';
}

export function useSystemStatus(enabled = true): SystemStatus {
  const client = useApiClient();
  const [detail, setDetail] = useState<SystemStatus['detail']>({});
  const [scanner, setScanner] = useState<StatusValue>('unknown');
  const [loading, setLoading] = useState<boolean>(enabled);
  const [token, setToken] = useState(0);

  const refresh = useCallback(() => setToken((t) => t + 1), []);

  // ── Server-side dependencies ───────────────────────────────────────
  useEffect(() => {
    if (!enabled || !client) return undefined;
    let live = true;
    setLoading(true);
    void client.health
      .dependencies()
      .then((res: DependenciesHealthResponse) => {
        if (!live) return;
        const next: SystemStatus['detail'] = {};
        for (const d of res?.dependencies ?? []) {
          if (!d?.name) continue;
          next[d.name] = {
            status: toStatus(d.status),
            ...(d.durationMs !== undefined ? { durationMs: d.durationMs } : {}),
            ...(d.error ? { error: d.error } : {}),
          };
        }
        setDetail(next);
      })
      .catch(() => {
        // The probe endpoint itself being unreachable tells us nothing about
        // any individual dependency, so leave them 'unknown' rather than
        // painting the whole board red.
        if (live) setDetail({});
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [client, enabled, token]);

  // ── Scanner Bridge (local only — the server cannot see it) ─────────
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const ctl = new AbortController();
    const timer = window.setTimeout(() => ctl.abort(), SCANNER_TIMEOUT_MS);
    void fetch(SCANNER_BRIDGE_URL, { signal: ctl.signal })
      .then((r) => {
        if (live) setScanner(r.ok ? 'ok' : 'down');
      })
      .catch(() => {
        if (live) setScanner('down');
      })
      .finally(() => window.clearTimeout(timer));
    return () => {
      live = false;
      ctl.abort();
      window.clearTimeout(timer);
    };
  }, [enabled, token]);

  return {
    // 'jxrl' IS Core Director — the jXchange REST-Legacy gateway on Banno.
    coreDirector: detail.jxrl?.status ?? 'unknown',
    lexisNexis: detail.ddp?.status ?? 'unknown',
    usps: detail.usps?.status ?? 'unknown',
    scannerBridge: scanner,
    detail,
    loading,
    refresh,
  };
}
